const APP_STATE_KEYS = [
  "gwrene_day_progress",
  "gwrene_pro_progress",
  "gwrene_pro_advanced_progress",
  "gwrene_badges_earned",
  "gwrene_riff_best",
  "gwrene_streak",
  "gwrene_practice_profile",
  "gwrene_today_session",
  "gwrene_recording_log",
  "gwrene_recording_takes",
  "gwrene_practice_history",
  "gwrene_boss_level",
  "gwrene_song_practice_best",
  "gwrene_song_drill_scores",
  "gwrene_chord_change_best",
  "gwrene_chord_check",
  "gwrene_last_member_tab",
  "gwrene_last_work_tab",
  "gwrene_last_library_tab",
  "gwrene_saved_items",
  "gwrene_tab_practiced",
  "gwrene_ear_training",
  "gwrene_metronome",
  "gwrene_tuner_tuning",
  "gwrene_starred_chord_practice",
  "gwrene_songwriting_ideas",
  "gwrene_recording_wizard",
  "gwrene_tone_fix",
  "gwrene_tone_builder",
  "gwrene_progression_best",
  "gwrene_course_started",
  "gwrene_last_course_day",
  "gwrene_practice_activity",
  "gwrene_goal_song",
  "gwrene_weekly_goal",
  "gwrene_practice_reminder",
  "gwrene_day1_stage",
  "gwrene_day1_verified",
  "gwrene_day1_jam_complete",
  "gwrene_day1_tuner_intro_seen",
  "gwrene_last_song_item",
  "gwrene_song_item_progress",
  "gwrene_song_medal",
];

export const ACCOUNT_SYNC_EVENT = "gwrene-local-progress-changed";

const LOCAL_PROGRESS_CHANGED_AT_KEY = "gwrene_local_progress_changed_at";

export function markLocalAppStateChanged() {
  const changedAt = new Date().toISOString();
  try {
    localStorage.setItem(LOCAL_PROGRESS_CHANGED_AT_KEY, changedAt);
  } catch {
    // Autosave can still run for this session if localStorage is unavailable.
  }
  window.dispatchEvent(new CustomEvent(ACCOUNT_SYNC_EVENT, { detail: { changedAt } }));
}

export function getLocalAppStateChangedAt() {
  try {
    return localStorage.getItem(LOCAL_PROGRESS_CHANGED_AT_KEY) || "";
  } catch {
    return "";
  }
}

export function collectLocalAppState() {
  const values = {};
  APP_STATE_KEYS.forEach((key) => {
    const value = localStorage.getItem(key);
    if (value !== null) values[key] = value;
  });
  return {
    version: 1,
    exportedAt: new Date().toISOString(),
    changedAt: getLocalAppStateChangedAt() || new Date().toISOString(),
    values,
  };
}

// True when the collected state contains real learner progress. A fresh
// browser only holds trivia like the last-open tab; it must never be allowed
// to overwrite a cloud backup that has actual progress in it.
const PROGRESS_STATE_KEYS = [
  "gwrene_day_progress",
  "gwrene_pro_progress",
  "gwrene_pro_advanced_progress",
  "gwrene_practice_history",
  "gwrene_saved_items",
  "gwrene_tab_practiced",
  "gwrene_ear_training",
  "gwrene_chord_check",
  "gwrene_starred_chord_practice",
  "gwrene_practice_activity",
  "gwrene_goal_song",
  "gwrene_song_item_progress",
  "gwrene_song_medal",
];

export function appStateHasProgress(state) {
  const values = state?.values && typeof state.values === "object" ? state.values : {};
  return PROGRESS_STATE_KEYS.some((key) => {
    const raw = values[key];
    if (typeof raw !== "string" || !raw) return false;
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.length > 0;
      if (parsed && typeof parsed === "object") {
        return Object.values(parsed).some((entry) =>
          Array.isArray(entry) ? entry.length > 0 : Boolean(entry),
        );
      }
      return Boolean(parsed);
    } catch {
      return false;
    }
  });
}

export function restoreLocalAppState(state) {
  const values = state?.values && typeof state.values === "object" ? state.values : {};
  Object.entries(values).forEach(([key, value]) => {
    if (!APP_STATE_KEYS.includes(key) || typeof value !== "string") return;
    localStorage.setItem(key, value);
  });
  markLocalAppStateChanged();
}

function summarizeValues(values = {}) {
  let savedItems = { chords: [], songs: [], tones: [], riffs: [], tabs: [] };
  let progress = {};
  let history = {};

  try {
    savedItems = JSON.parse(values.gwrene_saved_items) || savedItems;
  } catch {
    // Summary stays approximate if storage contains older data.
  }
  try {
    progress = JSON.parse(values.gwrene_day_progress) || {};
  } catch {
    // Summary stays approximate if storage contains older data.
  }
  try {
    history = JSON.parse(values.gwrene_practice_history) || {};
  } catch {
    // Summary stays approximate if storage contains older data.
  }

  return {
    completedLessons: Object.values(progress).filter(Boolean).length,
    savedCount: ["chords", "songs", "tones", "riffs", "tabs"].reduce(
      (total, key) => total + (Array.isArray(savedItems[key]) ? savedItems[key].length : 0),
      0,
    ),
    practiceDays: Object.keys(history).length,
  };
}

export function summarizeAppState(state) {
  const values = state?.values && typeof state.values === "object" ? state.values : {};
  return summarizeValues(values);
}

export function getLocalAppStateSummary() {
  const values = {};
  APP_STATE_KEYS.forEach((key) => {
    const value = localStorage.getItem(key);
    if (value !== null) values[key] = value;
  });
  return summarizeValues(values);
}
